import { MoreHorizontal, Pencil, Trash2, type LucideIcon } from 'lucide-react';
import { isValidElement, useState, type ReactNode } from 'react';
import { Button } from './Button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './Dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './DropdownMenu';
import { IconButton } from './IconButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './Tooltip';
import { cn } from './utils';

import { useLayoutPreferences, type TableActionsDisplayMode } from '../../hooks/useLayoutPreferences';

export type TableActionExtra = {
    key: string;
    label: string;
    icon: LucideIcon | ReactNode;
    onClick: () => void;
    variant?: 'default' | 'danger';
    disabled?: boolean;
};

export type TableActionsProps = {
    onEdit?: () => void;
    onDelete?: () => void | Promise<void>;
    deleteTitle?: string;
    deleteDescription?: string;
    deletePosition?: 'start' | 'end';
    extra?: TableActionExtra[];
    disabled?: boolean;
    className?: string;
    /** Exibe rótulos nos botões (obrigatório em Grid/Lista para touch e a11y). */
    variant?: 'icon' | 'labeled';
    /** Modo de exibição: 'auto' (segue preferência do usuário/empresa), 'buttons' ou 'dropdown'. */
    mode?: 'auto' | TableActionsDisplayMode;
};

function ActionIconButton({
    label,
    onClick,
    disabled,
    variant = 'default',
    children,
}: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: 'default' | 'danger';
    children: ReactNode;
}) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <IconButton
                    size="sm"
                    variant={variant}
                    label={label}
                    onClick={onClick}
                    disabled={disabled}
                >
                    {children}
                </IconButton>
            </TooltipTrigger>
            <TooltipContent side="top">{label}</TooltipContent>
        </Tooltip>
    );
}

function renderExtraIcon(icon: LucideIcon | ReactNode) {
    if (isValidElement(icon)) {
        return icon;
    }

    const Icon = icon as LucideIcon;
    return <Icon size={15} strokeWidth={1.9} aria-hidden />;
}

function LabeledActionButton({
    label,
    onClick,
    disabled,
    variant = 'default',
    children,
}: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: 'default' | 'danger';
    children: ReactNode;
}) {
    return (
        <Button
            type="button"
            variant={variant === 'danger' ? 'danger' : 'ghost'}
            className="nx-table-actions-labeled"
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
        >
            {children}
            <span>{label}</span>
        </Button>
    );
}

export function TableActions({
    onEdit,
    onDelete,
    deleteTitle = 'Excluir registro',
    deleteDescription = 'Tem certeza de que deseja excluir este registro? Esta ação não poderá ser desfeita.',
    deletePosition = 'end',
    extra = [],
    disabled = false,
    className,
    variant = 'icon',
    mode = 'auto',
}: TableActionsProps) {
    const { tableActionsMode: globalMode } = useLayoutPreferences();
    const effectiveMode: TableActionsDisplayMode =
        mode && mode !== 'auto' ? mode : (globalMode ?? 'buttons');

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    async function handleConfirmDelete() {
        if (!onDelete || deleting) return;
        setDeleting(true);
        try {
            await onDelete();
            setConfirmOpen(false);
        } catch {
            // A tela de origem apresenta a mensagem retornada pela API.
        } finally {
            setDeleting(false);
        }
    }

    const isLabeled = variant === 'labeled';
    const totalActions = (onEdit ? 1 : 0) + extra.length + (onDelete ? 1 : 0);

    if (totalActions === 0) {
        return null;
    }

    const renderDeleteButton = () =>
        onDelete &&
        (isLabeled ? (
            <LabeledActionButton
                label="Excluir"
                variant="danger"
                onClick={() => setConfirmOpen(true)}
                disabled={disabled}
            >
                <Trash2 size={15} strokeWidth={1.9} aria-hidden />
            </LabeledActionButton>
        ) : (
            <ActionIconButton
                label="Excluir"
                variant="danger"
                onClick={() => setConfirmOpen(true)}
                disabled={disabled}
            >
                <Trash2 size={15} strokeWidth={1.9} aria-hidden />
            </ActionIconButton>
        ));

    const renderDeleteMenuItem = () =>
        onDelete && (
            <DropdownMenuItem
                className="nx-dropdown-item--danger"
                disabled={disabled}
                onSelect={() => setConfirmOpen(true)}
            >
                <Trash2 size={15} strokeWidth={1.9} aria-hidden />
                Excluir
            </DropdownMenuItem>
        );

    return (
        <TooltipProvider delayDuration={250}>
            <div
                className={cn(
                    'nx-table-actions-group',
                    effectiveMode === 'buttons' && 'nx-table-actions-group--buttons',
                    effectiveMode === 'dropdown' && 'nx-table-actions-group--dropdown',
                    isLabeled && 'nx-table-actions-group--labeled',
                    className,
                )}
            >
                {effectiveMode === 'buttons' ? (
                    <>
                        {onEdit &&
                            (isLabeled ? (
                                <LabeledActionButton
                                    label="Editar"
                                    onClick={onEdit}
                                    disabled={disabled}
                                >
                                    <Pencil size={15} strokeWidth={1.9} aria-hidden />
                                </LabeledActionButton>
                            ) : (
                                <ActionIconButton label="Editar" onClick={onEdit} disabled={disabled}>
                                    <Pencil size={15} strokeWidth={1.9} aria-hidden />
                                </ActionIconButton>
                            ))}

                        {deletePosition === 'start' && renderDeleteButton()}

                        {extra.map((action) =>
                            isLabeled ? (
                                <LabeledActionButton
                                    key={action.key}
                                    label={action.label}
                                    variant={action.variant}
                                    onClick={action.onClick}
                                    disabled={disabled || action.disabled}
                                >
                                    {renderExtraIcon(action.icon)}
                                </LabeledActionButton>
                            ) : (
                                <ActionIconButton
                                    key={action.key}
                                    label={action.label}
                                    variant={action.variant}
                                    onClick={action.onClick}
                                    disabled={disabled || action.disabled}
                                >
                                    {renderExtraIcon(action.icon)}
                                </ActionIconButton>
                            ),
                        )}

                        {deletePosition === 'end' && renderDeleteButton()}
                    </>
                ) : (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            {isLabeled ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="nx-table-actions-labeled"
                                    disabled={disabled}
                                    aria-label="Ações"
                                >
                                    <MoreHorizontal size={15} strokeWidth={1.9} aria-hidden />
                                    <span>Ações</span>
                                </Button>
                            ) : (
                                <IconButton
                                    size="sm"
                                    variant="ghost"
                                    label="Ações"
                                    disabled={disabled}
                                >
                                    <MoreHorizontal size={16} strokeWidth={2} aria-hidden />
                                </IconButton>
                            )}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            sideOffset={8}
                            className="nx-row-actions-menu"
                        >
                            <DropdownMenuLabel>Ações</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {onEdit && (
                                <DropdownMenuItem
                                    disabled={disabled}
                                    onSelect={onEdit}
                                >
                                    <Pencil size={15} strokeWidth={1.9} aria-hidden />
                                    Editar
                                </DropdownMenuItem>
                            )}
                            {deletePosition === 'start' && renderDeleteMenuItem()}
                            {extra.map((action) => (
                                <DropdownMenuItem
                                    key={action.key}
                                    className={
                                        action.variant === 'danger'
                                            ? 'nx-dropdown-item--danger'
                                             : undefined
                                    }
                                    disabled={disabled || action.disabled}
                                    onSelect={action.onClick}
                                >
                                    {renderExtraIcon(action.icon)}
                                    {action.label}
                                </DropdownMenuItem>
                            ))}
                            {deletePosition === 'end' && renderDeleteMenuItem()}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>

            {onDelete && (
                <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <DialogContent
                        onOpenAutoFocus={(event) => {
                            event.preventDefault();
                            const content = event.currentTarget as HTMLElement | null;
                            content
                                ?.querySelector<HTMLButtonElement>('[data-confirm-delete]')
                                ?.focus();
                        }}
                    >
                        <DialogHeader>
                            <DialogTitle>{deleteTitle}</DialogTitle>
                            <DialogDescription>{deleteDescription}</DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <DialogClose asChild>
                                <Button type="button" variant="ghost" disabled={deleting}>
                                    Cancelar
                                </Button>
                            </DialogClose>
                            <Button
                                type="button"
                                variant="danger"
                                data-confirm-delete
                                disabled={deleting}
                                onClick={handleConfirmDelete}
                            >
                                {deleting ? 'Excluindo…' : 'Excluir'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </TooltipProvider>
    );
}
